import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, { forwardRef } from 'react';

import { cn } from '@/shared/classnames';

import { TouchTarget } from '../button';
import { Link } from '../link';

export function Sidebar({ className, ...props }: React.ComponentPropsWithoutRef<'nav'>) {
  return (
    <nav
      className={clsx(className, 'flex h-full min-h-0 flex-col p-2.5')}
      {...props}
    />
  );
}

export function SidebarHeader({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={cn(
        'flex flex-col border-b border-zinc-950/5 pb-2.5 dark:border-zinc-700 [&>[data-slot=section]+[data-slot=section]]:mt-2.5 overflow-hidden',
        className
      )}
    />
  );
}

export function SidebarBody({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-1 flex-col gap-1 pt-3 [&>[data-slot=section]+[data-slot=section]]:mt-8'
      )}
    />
  );
}

export function SidebarFooter({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-t border-zinc-950/5 pt-2.5 dark:border-zinc-700 [&>[data-slot=section]+[data-slot=section]]:mt-2.5'
      )}
    />
  );
}

export function SidebarSection({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div {...props} data-slot="section" className={clsx(className, 'flex flex-col gap-1')} />
  );
}

export function SidebarDivider({ className, ...props }: React.ComponentPropsWithoutRef<'hr'>) {
  return <hr {...props} className={clsx(className, 'border-t border-zinc-950/5 dark:border-zinc-700')} />;
}

export function SidebarSpacer({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'mt-8 flex-1')} />;
}

export function SidebarHeading({ className, ...props }: React.ComponentPropsWithoutRef<'h3'>) {
  return (
    <h3 {...props} className={clsx(className, 'mb-1 px-2 text-xs/6 font-medium text-zinc-500 dark:text-zinc-400')} />
  );
}

export const SidebarItem = forwardRef(function SidebarItem(
  {
    current,
    className,
    children,
    ...props
  }: { current?: boolean; className?: string; children: React.ReactNode } & (
    | Omit<Headless.ButtonProps, 'as' | 'className'>
    | Omit<Headless.ButtonProps<typeof Link>, 'as' | 'className'>
  ),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>
) {
  const classes = clsx(
    'flex w-full items-center gap-0.5 rounded-xl p-1 text-sm/5 font-normal overflow-hidden relative min-w-0',
    '*:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:fill-zinc-50 *:data-[slot=icon]:m-2',
    '*:last:data-[slot=icon]:ml-auto sm:*:last:data-[slot=icon]:size-4',
    '*:data-[slot=avatar]:m-2 *:data-[slot=avatar]:ml-1.5! *:data-[slot=avatar]:[--ring-opacity:10%] sm:*:data-[slot=avatar]:size-6',
    'data-hover:bg-zinc-800',
    'data-current:font-bold data-current:text-primary data-current:*:data-[slot=icon]:fill-primary data-current:bg-zinc-800'
  );

  return (
    <span className={clsx(className, 'relative')}>
      {'href' in props ?
          (
            <Headless.CloseButton
              as={Link}
              {...props}
              className={classes}
              data-current={current ? 'true' : undefined}
              ref={ref}
            >
              <TouchTarget>{children}</TouchTarget>
            </Headless.CloseButton>
          ) :
          (
            <Headless.Button
              {...props}
              className={clsx('cursor-default', classes)}
              data-current={current ? 'true' : undefined}
              ref={ref}
            >
              <TouchTarget>{children}</TouchTarget>
            </Headless.Button>
          )}
    </span>
  );
});

export function SidebarLabel({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return <span {...props} className={clsx(className, 'truncate min-w-0')} />;
}
